/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* eslint-disable security/detect-object-injection */
/* eslint-disable security/detect-unsafe-regex */

import * as React from 'react';

// @ts-ignore
import { Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IContext } from '@msdyn365-commerce/retail-proxy';
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { vSIInitiateRestCallAsync } from '../../dotdigital-extension/DataActionExtension.g';
import { IDobbiesVendorOnBoardProps } from './dobbies-vendor-on-board.props.autogenerated';

export interface IDobbiesVendorOnBoardViewProps extends IDobbiesVendorOnBoardProps<{}> {
    companyInfoForm: React.ReactNode;
    contactInfoForm: React.ReactNode;
    recaptcha: React.ReactNode;
    formButton: React.ReactNode;
    formFunction?: ((event: React.FormEvent<HTMLFormElement>) => void) | undefined;
    heading: React.ReactNode;
    description: React.ReactNode;
}

interface IVendorOnBoardInputProps {
    id?: string;
    // tslint:disable-next-line:no-any
    type: any;
    pattern?: string;
    gridClass?: string;
}

interface IState {
    companyName?: string;
    businessType?: string;
    companyInfo?: string;
    companyNumber?: string;
    companyWebsite?: string;
    country?: string;
    zipCode?: string;
    street?: string;
    city?: string;
    state?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    confirmEmail?: string;
    phoneNumber?: string;
    isBtnEnable?: boolean;
    submissionErrorMsg?: string;
    submissionSuccessMsg?: string;
}

interface IVendorData {
    '@odata.type': '#Microsoft.Dynamics.DataEntities.ProspectiveVendorRegistrationRequest';
    EmailAddress: string;
    OrganizationNumber?: string;
    RequestCompany?: string;
    BusinessLineDescription?: string;
    LanguageId?: string;
    ContactPersonMiddleName?: string;
    ProcessedDate?: string;
    SubmittedDate?: string;
    BusinessJustificationDescription?: string;
    ContactPersonFirstName?: string;
    ContactPersonLastName?: string;
    OrganizationType?: string;
    CompanyName?: string;
}

interface IElementProps {
    id?: string;
    // tslint:disable-next-line:no-any
    type: any;
    gridClass?: string;
}

/**
 *
 * DobbiesVendorOnBoard component
 * @extends {React.PureComponent<IDobbiesVendorOnBoardProps<{}>>}
 */
class DobbiesVendorOnBoard extends React.PureComponent<IDobbiesVendorOnBoardProps<{}>, IState> {
    private moduleClassName: string = 'ms-vendor-on-board';

    constructor(props: IDobbiesVendorOnBoardProps<{}>, state: IState) {
        super(props);

        this.state = {
            companyName: '',
            businessType: '',
            companyInfo: '',
            companyNumber: '',
            companyWebsite: '',
            country: '',
            zipCode: '',
            street: '',
            city: '',
            state: '',
            firstName: '',
            lastName: '',
            email: '',
            confirmEmail: '',
            phoneNumber: '',
            isBtnEnable: false
        };
    }

    public onFormSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();

        const {
            companyName = '',
            businessType = '',
            companyInfo = '',
            companyNumber = '',
            companyWebsite = '',
            country = '',
            zipCode = '',
            street = '',
            city = '',
            state = '',
            firstName = '',
            lastName = '',
            email = '',
            confirmEmail = '',
            phoneNumber = '',
            isBtnEnable
        }: IState = this.state;

        if (isBtnEnable) {
            const data = {
                companyName,
                businessType,
                companyInfo,
                companyNumber,
                companyWebsite,
                country,
                zipCode,
                street,
                city,
                state,
                firstName,
                lastName,
                email,
                confirmEmail,
                phoneNumber
            };

            const requiredFieds = [
                'companyName',
                'businessType',
                'companyInfo',
                'country',
                'firstName',
                'lastName',
                'email',
                'confirmEmail'
            ];

            document.querySelectorAll('.ms-vendor-error').forEach(e => e.remove());
            let isError: boolean = false;

            requiredFieds.forEach(field => {
                if (!data[field]) {
                    this.addError(field, this.props.resources.requiredFieldMissingError);
                    isError = true;
                }
            });

            if (!isError) {
                const emailRegex = new RegExp(
                    '^[a-zA-Z0-9!#$%&amp;&#39;+^_`{}~-]+(?:.[a-zA-Z0-9!#$%&amp;&#39;+^_`{}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$'
                );

                if (!emailRegex.test(email)) {
                    this.addError('email', this.props.resources.invalidEmailAddressError);
                    isError = true;
                }
                if (!emailRegex.test(confirmEmail)) {
                    this.addError('confirmEmail', this.props.resources.invalidEmailAddressError);
                    isError = true;
                }
                if (email !== confirmEmail) {
                    this.addError('confirmEmail', this.props.resources.emailEntryMismatchError);
                    isError = true;
                }
                if (!isError) {
                    // First hybrid vendorObject
                    const today = new Date();
                    const formattedDate = today.toISOString();
                    const vendorData: IVendorData = {
                        '@odata.type': '#Microsoft.Dynamics.DataEntities.ProspectiveVendorRegistrationRequest',
                        EmailAddress: email,
                        OrganizationNumber: companyNumber,
                        RequestCompany: 'DGC',
                        BusinessLineDescription: businessType,
                        LanguageId: 'en-GB',
                        ContactPersonFirstName: firstName,
                        ContactPersonLastName: lastName,
                        ContactPersonMiddleName: firstName,
                        // ProcessedDate: formattedDate, // today's date in this data_formatted '1900-01-01T00:00:00Z'
                        SubmittedDate: formattedDate, // today's date in this data_formatted
                        BusinessJustificationDescription: companyInfo, // rich text field
                        OrganizationType: 'Organization', // add optional field
                        CompanyName: companyName
                    };
                    this._registerVendor(vendorData).catch(error => error);
                }
            }
        }
    };
    // Following function first calls retail proxy to get token and then sends this token in headers of second request to register vendor
    public _registerVendor = async (vendorInfo: IVendorData) => {
        const {
            context: { actionContext },
            resources: { submissionErrorMessage, submissionSuccessMessage }
        } = this.props;

        try {
            const context: IContext = { callerContext: actionContext };
            console.log('Api Data :>> ', vendorInfo);
            // const requestHeader = {
            //     grant_type: 'client_credentials'
            // };
            const vendorResponse = await vSIInitiateRestCallAsync(
                context,
                'D365HQ',
                'post',
                "{'TargetProviderSystem':'STSWindows' , 'ProviderType':'Microsoft'}",
                JSON.stringify(vendorInfo),
                '',
                'data/ProspectiveVendorRegistrationRequests'
            );
            if (vendorResponse.result) {
                const vendorSubmissionError = JSON.parse(vendorResponse.result);
                if (vendorSubmissionError.error && vendorSubmissionError.error.message) {
                    //  Error in getting token
                    console.log('error in submitting vendor details');
                    this.setState({ submissionErrorMsg: submissionErrorMessage, submissionSuccessMsg: undefined });
                    return;
                } else {
                    this.setState({ submissionSuccessMsg: submissionSuccessMessage, submissionErrorMsg: '' });
                    console.log('submitted', vendorResponse);
                    // Now clear all fields
                }
            }
            // First get token
            // const tokenResponse = await vSIInitiateRestCallAsync(context, 'Stswindows', 'get', '"cache-control","no-cache"', JSON.stringify(requestHeader), '', '');
            // if (tokenResponse.result) {
            //     const errorMessage: IErrorMessage = JSON.parse(tokenResponse.result);
            //     if (errorMessage.message && errorMessage.message.length > 0) {
            //         //  Error in getting token
            //         console.log('error in getting token');
            //         this.setState({ submissionErrorMsg: submissionErrorMessage, submissionSuccessMsg: undefined });
            //         return;
            //     } else {
            //         /* Call api to register vendor with this token & vendor data,
            //         Send this token in headers to submit vendor registration */
            //         const vendorResponse = await vSIInitiateRestCallAsync(context, 'Stswindows', 'get', '"cache-control","no-cache"', JSON.stringify(vendorInfo), '', '');
            //         const vendorSubmissionError: IErrorMessage = JSON.parse(tokenResponse.result);
            //         if (vendorSubmissionError.message && vendorSubmissionError.message.length > 0) {
            //             //  Error in getting token
            //             console.log('error in submitting vendor details');
            //             this.setState({ submissionErrorMsg: submissionErrorMessage, submissionSuccessMsg: undefined });
            //             return;
            //         } else {
            //             this.setState({ submissionSuccessMsg: submissionSuccessMessage, submissionErrorMsg: undefined });
            //             console.log('submitted', vendorResponse);
            //             // Now clear all fields

            //         }
            //     }
            // } else {
            //     this.setState({ submissionErrorMsg: submissionErrorMessage, submissionSuccessMsg: undefined });
            //     console.log('error in calling api');
            // }
        } catch (error) {
            this.props.telemetry.trace(error);
            this.setState({ submissionErrorMsg: submissionErrorMessage, submissionSuccessMsg: undefined });
            console.log('error in calling api', error);
        }
    };
    /**
     * this function is for rendering valiadtion error messages
     * @props element: string, message: string
     */
    public addError = (elem: string, message: string) => {
        const parent = document.getElementById(elem);
        const error = document.createElement('SPAN');
        error.innerText = message;
        error.classList.add('ms-vendor-error');
        parent?.parentNode!.appendChild(error);
    };

    public render(): JSX.Element | null {
        const { config, context } = this.props;

        const propsData = {
            ...this.props,
            formFunction: this.onFormSubmit,
            companyInfoForm: this.formSections('company'),
            contactInfoForm: this.formSections('contact'),
            recaptcha: this.captcha(),
            formButton: this._renderButton(),
            heading: (
                <Msdyn365.Text
                    className={`${this.moduleClassName}__heading`}
                    {...config.heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: context.request }}
                />
            ),
            description: config.description && (
                <Msdyn365.RichTextComponent
                    className={`${this.moduleClassName}__description`}
                    text={config.description}
                    editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
                />
            )
        };
        return this.props.renderView(propsData);
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.description = event.target.value);

    /**
     * name
     */
    public formSections = (type: string) => {
        const types = {
            company: [
                { id: 'companyName', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'businessType', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'companyInfo', type: 'text', pattern: undefined, gridClass: 'col-md-12 col-sm-12' },
                { id: 'companyNumber', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'companyWebsite', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'country', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'zipCode', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'street', type: 'input', pattern: undefined, gridClass: 'col-md-12 col-sm-12' },
                { id: 'city', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'state', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' }
            ],
            contact: [
                { id: 'firstName', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'lastName', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'email', type: 'email', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'confirmEmail', type: 'email', pattern: undefined, gridClass: 'col-md-6 col-sm-12' },
                { id: 'phoneNumber', type: 'input', pattern: undefined, gridClass: 'col-md-6 col-sm-12' }
            ]
        };

        return types[type].map((elem: IElementProps) => this._renderInput(elem));
    };

    public onChangeInputState = (stateName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setState({ [stateName]: event.target.value });
    };
    /**
     * name
     */
    public _renderInput = (props: IVendorOnBoardInputProps) => {
        const { id = '', type, pattern, gridClass } = props;
        const className = `${this.moduleClassName}__item`;
        // tslint:disable-next-line: no-constant-condition
        if (type !== 'text') {
            return (
                <div className={gridClass}>
                    <label htmlFor={id} className={`${className}-label`}>
                        {this.props.resources[id]}
                    </label>
                    <input
                        id={id}
                        type={type}
                        value={this.state[id]}
                        className={`${className}-input ${className}-${id}`}
                        pattern={pattern !== '' ? pattern : undefined}
                        aria-required='true'
                        onChange={this.onChangeInputState(id)}
                    />
                </div>
            );
        } else {
            return (
                <div className={gridClass}>
                    <label htmlFor={id} className={`${className}-label`}>
                        {this.props.resources[id]}
                    </label>
                    <textarea
                        id={id}
                        value={this.state[id]}
                        className={`${className}-input ${className}-${id}`}
                        aria-required='true'
                        onChange={this.onChangeInputState(id)}
                        // placeholder={this.props.resources[id]}
                    />
                </div>
            );
        }
    };

    public captcha = () => {
        const key = this.props.config.googleSiteKey;
        return (
            <div className='captchaDiv'>
                <GoogleReCaptchaProvider reCaptchaKey={key!}>
                    <GoogleReCaptcha onVerify={this.onCaptchaChange} />
                </GoogleReCaptchaProvider>
            </div>
        );
    };

    public _renderButton = () => {
        const { isBtnEnable, submissionErrorMsg, submissionSuccessMsg } = this.state;
        return (
            <div className='col-12'>
                {submissionErrorMsg && <Node className='submission-error-message'>{submissionErrorMsg}</Node>}
                {submissionSuccessMsg && <Node className='submission-success-message'>{submissionSuccessMsg}</Node>}
                <input
                    type='submit'
                    className={`${this.moduleClassName}__button`}
                    disabled={!isBtnEnable}
                    onClick={this.onFormSubmit}
                    value={this.props.resources.buttonText}
                />
            </div>
        );
    };

    public onCaptchaChange = () => {
        this.setState({
            isBtnEnable: true
        });
    };
}

export default DobbiesVendorOnBoard;
